import dayjs from "dayjs";
import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import DatepickerInput from "./DatepickerInput";
import { asDayString } from "../../services/utility";

import "react-datepicker/dist/react-datepicker.css";

const PAYMENT_TYPES = [
    { value: "cash", label: "Cash" },
    { value: "bank", label: "Bank" },
];

const formatBaht = n =>
    `฿${Math.round(n).toLocaleString("en-US")}`;

/* Correcting a sale already entered. Seven things can change: who it is
   credited to, which shop, cash or bank, the day, the time, what each line
   cost, and the commission rate.

   What cannot: which service a line is, and how many lines there are. That is
   the till's job, and building it twice would be two places for the rules
   about a sale to disagree. */
const EditSale = ({
    sale,
    staffOptions,
    staffObj,
    shopOptions,
    onCancel,
    onSave,
    onDelete,
}) => {
    const [staff, setStaff] = useState("");
    const [shop, setShop] = useState("");
    const [payment, setPayment] = useState("");
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [prices, setPrices] = useState([]);
    /* held as the percentage that is typed, not the fraction that is stored.
       A box holding 0.4 invites someone meaning 40% to type 40, and a rate of
       40 would multiply a month's commission by a hundred while still looking
       like a number. */
    const [ratePercent, setRatePercent] = useState("");

    /* Filled from the row each time a different sale is opened. Keyed on the
       id rather than the object, so a refreshed list does not throw away
       half-finished edits to the same sale. */
    useEffect(() => {
        if (!sale) {
            return;
        }
        setStaff(staffOptions?.find(o => o.value === sale.staffId) || "");
        setShop(shopOptions?.find(o => o.value === sale.shopId) || "");
        setPayment(
            PAYMENT_TYPES.find(o => o.value === sale.paymentType) || "",
        );
        setDate(dayjs(sale.day).toISOString());
        setTime(sale.time || "");
        setPrices((sale.lines || []).map(({ price }) => String(price)));
        setRatePercent(String(Math.round((sale.rate || 0) * 1000) / 10));
    }, [sale?._id]);

    if (!sale) {
        return null;
    }

    const total = prices.reduce((acc, p) => acc + (Number(p) || 0), 0);
    const rate = Number(ratePercent) / 100;
    const rateIsValid =
        ratePercent !== "" && Number.isFinite(rate) && rate >= 0 && rate <= 1;
    const commission = rateIsValid ? total * rate : 0;

    /* A sale that predates shops may stay without one, as an expense may:
       those cannot be attributed now with any accuracy. One that has a shop
       cannot lose it. */
    const shopRequired = Boolean(sale.shopId);

    /* Empty and wrong are different things, and the button has to say which.
       "Fill in every field" in front of a rate of 120 sends you looking for
       a field you have already filled in.
       Nothing else here can be wrong rather than missing: the inputs refuse
       anything but a number at or above zero as it is typed, the selects
       only offer what exists, and the date comes from a picker. A rate over
       100% is the one bad value that can be entered, because the field has
       to accept two digits on the way to any of them. */
    const somethingMissing =
        !staff ||
        !payment ||
        !date ||
        ratePercent === "" ||
        prices.some(p => p === "") ||
        (!shop && shopRequired);

    const problem = somethingMissing
        ? "Fill in every field"
        : rateIsValid
          ? null
          : "Commission must be between 0% and 100%";
    const isValid = !problem;

    /* The rate is never moved on the operator's behalf -- not even when the
       sale is credited to someone else, where it is most tempting. But the
       most likely correction is between the owner, whose rate is 0, and a
       stylist, whose rate is not, so leaving it silent would turn a fixed
       attribution into a wrong commission. Shown, not changed. */
    const currentRate = staff ? staffObj?.[staff.value]?.commission : undefined;
    const rateMismatch =
        rateIsValid &&
        currentRate !== undefined &&
        Math.abs(currentRate - rate) > 0.0001;

    /* What the note says depends on whether the rate has been touched yet.
       Untouched, the point is that nothing will happen to it on its own.
       Once it has been set deliberately, saying it will stay "unless you
       change it" is stale -- it has been changed, and the note should report
       where it now stands rather than offer again.
       A rate that matches the staff member's raises no note at all. */
    const asPercent = n => `${Math.round(n * 1000) / 10}%`;
    const rateIsUntouched =
        ratePercent === String(Math.round((sale.rate || 0) * 1000) / 10);
    const rateNote = !rateMismatch
        ? null
        : rateIsUntouched
          ? `${staff.label} is currently on ${asPercent(
                currentRate,
            )}. This sale will stay at ${asPercent(rate)} unless you change it.`
          : `${staff.label} is currently on ${asPercent(
                currentRate,
            )}. This sale is now set to ${asPercent(rate)}.`;

    /* Refused as it is typed rather than caught on the way out, so the only
       thing the button ever has to complain about is a rate over 100 -- the
       one bad value that cannot be blocked at entry, since 120 and 100 look
       the same until the third character. A minus was getting through here:
       Number("-5") is finite, so the old guard passed it and validation
       failed afterwards, which is exactly the confusion this avoids. */
    const refuseIfNotMoney = value =>
        Boolean(value) &&
        (!Number.isFinite(Number(value)) || Number(value) < 0);

    const onChangePrice = (i, value) => {
        if (refuseIfNotMoney(value)) {
            return;
        }
        setPrices(prices.map((p, n) => (n === i ? value : p)));
    };

    const onChangeRate = value => {
        if (refuseIfNotMoney(value)) {
            return;
        }
        setRatePercent(value);
    };

    const onClickSave = () => {
        onSave({
            saleId: sale._id,
            staffId: staff.value,
            shopId: shop ? shop.value : "",
            paymentType: payment.value,
            commission: rate,
            day: asDayString(date),
            time,
            prices: prices.map(Number),
        });
    };

    const selectStyles = {
        control: baseStyles => ({
            ...baseStyles,
            boxShadow: "0 2px 4px rgba(0,0,0, .2)",
            border: "0",
            minHeight: "32px",
        }),
    };

    return (
        <div className="edit-sale">
            <div className="edit-sale-title">
                Edit sale of {sale.day} at {sale.time}
            </div>

            {/* No labels above any of these. A populated field says what it
                is -- a stylist's name, a shop, Cash, a date -- and seven
                labels cost more vertical space than the fields they name,
                which on a phone is the difference between reaching the save
                button and scrolling for it. The one exception is the
                commission, which carries its % inline below: a bare 40 next
                to a total of ฿1,200 could be read as an amount. */}
            <div className="edit-sale-field">
                <Select
                    options={staffOptions}
                    value={staff}
                    onChange={opt => setStaff(opt)}
                    styles={selectStyles}
                />
            </div>

            {rateNote ? (
                <div className="edit-sale-note">{rateNote}</div>
            ) : null}

            <div className="edit-sale-field">
                <Select
                    options={shopOptions}
                    value={shop}
                    onChange={opt => setShop(opt)}
                    placeholder={
                        shopRequired ? "Select shop" : "No shop recorded"
                    }
                    styles={selectStyles}
                />
            </div>

            <div className="edit-sale-row">
                <div className="edit-sale-field">
                    <Select
                        options={PAYMENT_TYPES}
                        value={payment}
                        onChange={opt => setPayment(opt)}
                        styles={selectStyles}
                    />
                </div>

                <div className="edit-sale-field">
                    <DatePicker
                        selected={date}
                        onChange={d => setDate(d)}
                        dateFormat="YYYY-MM-dd"
                        customInput={
                            <DatepickerInput className="datepicker-input" />
                        }
                        wrapperClassName="datepicker-wrapper"
                    />
                </div>

                <div className="edit-sale-field edit-sale-field-narrow">
                    <input
                        className="edit-sale-input"
                        type="text"
                        value={time}
                        placeholder="14:30"
                        onChange={e => setTime(e.target.value)}
                    />
                </div>
            </div>

            {/* the service names are shown but cannot be changed: what a line
                is for is the till's record, and only what it cost is in
                question here */}
            {(sale.lines || []).map(({ service }, i) => (
                <div className="edit-sale-line" key={`${service}-${i}`}>
                    <div className="edit-sale-line-name">{service}</div>
                    <input
                        className="edit-sale-input edit-sale-price"
                        type="text"
                        value={prices[i] ?? ""}
                        onChange={e => onChangePrice(i, e.target.value)}
                    />
                </div>
            ))}

            <div className="edit-sale-row">
                <div className="edit-sale-field edit-sale-field-narrow edit-sale-rate">
                    <input
                        className="edit-sale-input edit-sale-rate-input"
                        type="text"
                        value={ratePercent}
                        onChange={e => onChangeRate(e.target.value)}
                    />
                    <span className="edit-sale-rate-sign">%</span>
                </div>
                <div className="edit-sale-field">
                    <div className="edit-sale-derived">
                        {formatBaht(total)}
                    </div>
                </div>
            </div>

            <div style={{ marginTop: "7px" }}>
                <button
                    type="button"
                    className="submit-commission"
                    disabled={!isValid}
                    onClick={onClickSave}
                    style={{
                        opacity: isValid ? 1 : 0.4,
                        cursor: isValid ? "pointer" : "default",
                    }}
                >
                    {problem || `Save — commission ${formatBaht(commission)}`}
                </button>
            </div>

            <div className="expenses-edit-actions">
                <button
                    type="button"
                    className="expenses-cancel-button"
                    onClick={onCancel}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="admin-orphan-button"
                    onClick={onDelete}
                >
                    Delete
                </button>
            </div>
        </div>
    );
};

export default EditSale;
